import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeUpdate, TransactionProcessedData } from '@/services/realtime';
import { transformTransactionRow, type TransactionListResponse } from '@/services/transactions';
import type { Database } from '@/types/database';

type TransactionTableRow = Database['public']['Tables']['transactions']['Row'];

type TransactionQueryResult = TransactionListResponse | undefined;

type TransactionUpdater = (current: TransactionQueryResult) => TransactionQueryResult;

const isTransactionList = (value: unknown): value is TransactionListResponse => {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as TransactionListResponse).transactions) &&
    typeof (value as TransactionListResponse).totalCount === 'number'
  );
};

const applyTransactionUpdate = (
  queryClient: QueryClient,
  update: TransactionProcessedData
) => {
  const { eventType } = update;
  const newRecord = (update.changes?.new ?? update.record) as TransactionTableRow | null;
  const oldRecord = (update.changes?.old ?? update.record) as TransactionTableRow | null;
  const targetId = newRecord?.id ?? oldRecord?.id;

  if (!targetId) {
    return;
  }

  const transformedNewRow = newRecord ? transformTransactionRow(newRecord) : null;

  const updater: TransactionUpdater = (current) => {
    if (!isTransactionList(current)) {
      return current;
    }

    const index = current.transactions.findIndex((txn) => txn.id === targetId);

    if (eventType === 'DELETE') {
      if (index === -1) {
        return current;
      }

      const transactions = current.transactions.filter((txn) => txn.id !== targetId);
      const totalCount = Math.max(0, current.totalCount - 1);

      return {
        ...current,
        transactions,
        totalCount,
        hasNextPage: totalCount > transactions.length,
        hasPreviousPage: current.hasPreviousPage && transactions.length > 0,
      };
    }

    if (!transformedNewRow) {
      return current;
    }

    if (index === -1) {
      if (eventType === 'INSERT' && current.currentPage === 1) {
        const transactions = [transformedNewRow, ...current.transactions];
        const pageSize = Math.max(current.transactions.length, 1);
        const trimmed = transactions.slice(0, pageSize);

        return {
          ...current,
          transactions: trimmed,
          totalCount: current.totalCount + 1,
          hasNextPage: current.totalCount + 1 > pageSize,
          hasPreviousPage: current.hasPreviousPage,
        };
      }

      return current;
    }

    const transactions = [...current.transactions];
    transactions[index] = {
      ...transactions[index],
      ...transformedNewRow,
    };

    return {
      ...current,
      transactions,
    };
  };

  queryClient.setQueriesData({ queryKey: ['transactions'] }, updater);
};

const applyProcessingStatusUpdate = (
  queryClient: QueryClient,
  data: RealtimeUpdate['data']
) => {
  // Update file upload status queries
  queryClient.setQueriesData({ queryKey: ['file-uploads'] }, (current: any) => {
    if (!current) return current;
    return current; // File upload status is handled via separate query
  });

  // Invalidate dashboard metrics when processing completes
  if ((data as any).status === 'completed') {
    queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
  }
};

const applyFileUploadStatusUpdate = (
  queryClient: QueryClient,
  data: RealtimeUpdate['data']
) => {
  const uploadData = data as any;

  // Optimistic update for file upload progress
  queryClient.setQueryData(['file-upload', uploadData.uploadId], uploadData);

  // When upload completes, invalidate transactions to fetch new data
  if (uploadData.status === 'completed') {
    queryClient.invalidateQueries({ queryKey: ['transactions'], refetchType: 'active' });
  }
};

const applyComplianceAlertUpdate = (
  queryClient: QueryClient,
  data: RealtimeUpdate['data']
) => {
  // Add new compliance alert to the cache
  queryClient.setQueriesData({ queryKey: ['compliance-alerts'] }, (current: any) => {
    if (!current || !Array.isArray(current)) return [data];
    return [data, ...current];
  });
};

const applyTeamActivityUpdate = (
  queryClient: QueryClient,
  data: RealtimeUpdate['data']
) => {
  // Add new team activity to the cache
  queryClient.setQueriesData({ queryKey: ['team-activity'] }, (current: any) => {
    if (!current || !Array.isArray(current)) return [data];
    // Keep last 50 activities
    return [data, ...current.slice(0, 49)];
  });
};

export const applyRealtimeCacheUpdate = (
  queryClient: QueryClient,
  update: RealtimeUpdate
) => {
  try {
    switch (update.type) {
      case 'transaction_processed':
        applyTransactionUpdate(queryClient, update.data);
        break;

      case 'processing_status':
        applyProcessingStatusUpdate(queryClient, update.data);
        break;

      case 'file_upload_status':
        applyFileUploadStatusUpdate(queryClient, update.data);
        break;

      case 'compliance_alert':
        applyComplianceAlertUpdate(queryClient, update.data);
        break;

      case 'team_activity':
        applyTeamActivityUpdate(queryClient, update.data);
        break;

      default:
        // Unknown event type - log but don't fail
        console.debug('Unhandled realtime event type:', (update as any).type);
        break;
    }
  } catch (error) {
    console.error('Realtime cache update failed', error);
  }
};
